'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { EntityDetailSkeleton } from './skeletons/EntityDetailSkeleton'
import { ClustersListSkeleton } from './skeletons/ClustersListSkeleton'
import { ListingSkeleton } from './skeletons/ListingSkeleton'
import { MapPageSkeleton } from './skeletons/MapPageSkeleton'
import { TransportationSkeleton } from './skeletons/TransportationSkeleton'
import { HomeSkeleton } from './skeletons/HomeSkeleton'

type SkeletonKind = 'home' | 'clustersList' | 'entityDetail' | 'listing' | 'map' | 'transportation' | null

const CATEGORIES: { prefix: string; verb: string; fallback: string }[] = [
  { prefix: '/clusters',       verb: 'Loading',        fallback: 'Exploring Goris...' },
  { prefix: '/events',         verb: 'Discovering',    fallback: 'Discovering upcoming events...' },
  { prefix: '/places',         verb: 'Exploring',      fallback: 'Exploring places...' },
  { prefix: '/people',         verb: 'Meeting',        fallback: 'Meeting the people of Goris...' },
  { prefix: '/services',       verb: 'Finding',        fallback: 'Finding services...' },
  { prefix: '/institutions',   verb: 'Exploring',      fallback: 'Exploring institutions...' },
  { prefix: '/news',           verb: 'Catching up on', fallback: 'Catching up on news...' },
  { prefix: '/map',            verb: 'Opening',        fallback: 'Opening the map...' },
  { prefix: '/transportation', verb: 'Checking',       fallback: 'Checking transportation...' },
]

const ENTITY_LINK_CLASSES = ['entity-card', 'rel-card', 'ed-explore-card', 'hp-ev-small', 'hp-ev-feat']
const NAME_SELECTORS = '.entity-name, .name, .ed-explore-name, .hp-ev-feat-title, .hp-ev-small-title'

function isEntityLink(el: HTMLElement): boolean {
  return ENTITY_LINK_CLASSES.some(c => el.classList.contains(c))
}

function extractName(el: HTMLElement): string {
  const nameEl = el.querySelector(NAME_SELECTORS)
  const text = (nameEl?.textContent ?? el.textContent ?? '').trim()
  return text.split('\n')[0].trim()
}

function computeTarget(anchor: HTMLAnchorElement): { message: string; skeleton: SkeletonKind } {
  const override = anchor.dataset.loadingMessage
  const path = new URL(anchor.href).pathname

  if (path === '/') {
    return { message: override ?? 'Returning to Goris...', skeleton: 'home' }
  }

  const category = CATEGORIES.find(c => path === c.prefix || path.startsWith(`${c.prefix}/`))
  if (!category) {
    return { message: override ?? 'Preparing your journey...', skeleton: null }
  }

  const isDetail = path.length > category.prefix.length + 1
  let message = override ?? category.fallback
  if (!override && isDetail && isEntityLink(anchor)) {
    const name = extractName(anchor)
    if (name) message = `${category.verb} ${name}...`
  }

  let skeleton: SkeletonKind = 'listing'
  if (category.prefix === '/clusters') skeleton = isDetail ? 'entityDetail' : 'clustersList'
  else if (category.prefix === '/map') skeleton = 'map'
  else if (category.prefix === '/transportation') skeleton = 'transportation'
  else skeleton = isDetail ? 'entityDetail' : 'listing'

  return { message, skeleton }
}

function SkeletonFor({ kind }: { kind: SkeletonKind }) {
  switch (kind) {
    case 'home': return <HomeSkeleton />
    case 'clustersList': return <ClustersListSkeleton />
    case 'entityDetail': return <EntityDetailSkeleton />
    case 'listing': return <ListingSkeleton />
    case 'map': return <MapPageSkeleton />
    case 'transportation': return <TransportationSkeleton />
    default: return null
  }
}

const MESSAGE_PHASE_MS = 450
const SAFETY_TIMEOUT_MS = 6000

export function PageTransitionOverlay() {
  const pathname = usePathname()
  const [state, setState] = useState<{
    visible: boolean
    phase: 'message' | 'skeleton'
    message: string
    skeleton: SkeletonKind
  }>({ visible: false, phase: 'message', message: '', skeleton: null })

  const pendingFrom = useRef<string | null>(null)
  const phaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = () => {
    if (phaseTimer.current) clearTimeout(phaseTimer.current)
    if (safetyTimer.current) clearTimeout(safetyTimer.current)
  }

  useEffect(() => {
    if (pendingFrom.current !== null && pendingFrom.current !== pathname) {
      pendingFrom.current = null
      clearTimers()
      setState(s => ({ ...s, visible: false }))
    }
  }, [pathname])

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (e.defaultPrevented || e.button !== 0) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

      const anchor = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      if (anchor.target && anchor.target !== '_self') return
      if (anchor.hasAttribute('download')) return

      let url: URL
      try {
        url = new URL(anchor.href)
      } catch {
        return
      }
      if (url.origin !== window.location.origin) return
      if (url.pathname === window.location.pathname) return

      const { message, skeleton } = computeTarget(anchor)
      pendingFrom.current = window.location.pathname
      clearTimers()
      setState({ visible: true, phase: 'message', message, skeleton })

      if (skeleton) {
        phaseTimer.current = setTimeout(() => {
          setState(s => ({ ...s, phase: 'skeleton' }))
        }, MESSAGE_PHASE_MS)
      }

      safetyTimer.current = setTimeout(() => {
        pendingFrom.current = null
        setState(s => ({ ...s, visible: false }))
      }, SAFETY_TIMEOUT_MS)
    }

    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  const showSkeleton = state.visible && state.phase === 'skeleton' && state.skeleton

  return (
    <div
      className={`ptx-overlay${state.visible ? ' ptx-overlay--visible' : ''}${showSkeleton ? ' ptx-overlay--skeleton' : ''}`}
      aria-hidden={!state.visible}
    >
      {showSkeleton ? (
        <div className="ptx-skeleton-wrap">
          <SkeletonFor kind={state.skeleton} />
        </div>
      ) : (
        <div className="ptx-message">
          <span className="ptx-dot" />
          {state.message}
        </div>
      )}
    </div>
  )
}
